/**
 * EmbeddableGallery — A standalone widget that influencers can embed
 * on their website/blog to showcase live game submissions and drive votes.
 *
 * Usage: <EmbeddableGallery gameSlug="best-drawing-challenge-abc123" limit={6} />
 *
 * This component is designed to work both:
 * 1. Inside the GamePoint app (as a component on the GameDetail page)
 * 2. As an embeddable iframe widget (via /embed/:slug route)
 */

import { trpc } from "@/lib/trpc";
import { motion } from "framer-motion";
import {
  Trophy, Heart, ExternalLink, Clock, Users, Share2, Image, FileText, Video
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SafeImage from "@/components/SafeImage";
import { toast } from "sonner";
import { useState } from "react";

interface EmbeddableGalleryProps {
  gameSlug: string;
  limit?: number;
  compact?: boolean; // Compact mode for sidebar/widget use
  showHeader?: boolean;
  onVote?: (submissionId: number) => void;
}

function SubmissionTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "image": return <Image className="w-3.5 h-3.5" />;
    case "video": return <Video className="w-3.5 h-3.5" />;
    case "text": return <FileText className="w-3.5 h-3.5" />;
    default: return <FileText className="w-3.5 h-3.5" />;
  }
}

function SubmissionCard({
  submission,
  rank,
  compact,
  gameSlug,
  onVote,
}: {
  submission: {
    id: number;
    content: string;
    submissionType: string;
    caption: string | null;
    voteCount: number;
    shareToken: string;
    featured: number | boolean;
  };
  rank: number;
  compact: boolean;
  gameSlug: string;
  onVote?: (id: number) => void;
}) {
  const [voted, setVoted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const voteMutation = trpc.game.vote.useMutation();

  const handleVote = async () => {
    if (voted) return;
    try {
      // Persist the vote first — the widget exists to drive real votes.
      await voteMutation.mutateAsync({ submissionId: submission.id });
      setVoted(true);
      setAnimating(true);
      onVote?.(submission.id);
      setTimeout(() => setAnimating(false), 600);
    } catch (err: any) {
      if (err?.message?.includes("already voted") || err?.data?.code === "CONFLICT") {
        setVoted(true);
        toast.info("You've already voted for this entry");
      } else {
        toast.error(err?.message || "Vote failed. Please try again.");
      }
    }
  };

  const shareUrl = `${window.location.origin}/game/${gameSlug}/entry/${submission.shareToken}`;

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `Vote for this entry!`,
        text: submission.caption || "Check out this submission and vote!",
        url: shareUrl,
      });
    } else {
      await navigator.clipboard.writeText(shareUrl);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.05 }}
      className={`relative group rounded-xl overflow-hidden border border-border/50 bg-card hover:border-neon/30 transition-all ${
        compact ? "p-2" : "p-3"
      } ${submission.featured ? "ring-1 ring-gold/40" : ""}`}
    >
      {/* Rank badge */}
      {rank <= 3 && (
        <div className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
          rank === 1 ? "bg-gold text-background" :
          rank === 2 ? "bg-gray-300 text-background" :
          "bg-amber-700 text-white"
        }`}>
          {rank}
        </div>
      )}

      {/* Featured badge */}
      {submission.featured && (
        <Badge className="absolute top-2 right-2 z-10 bg-gold/20 text-gold border-gold/30 text-[9px]">
          Featured
        </Badge>
      )}

      {/* Content preview */}
      <div className={`${compact ? "mb-2" : "mb-3"}`}>
        {submission.submissionType === "image" ? (
          <div className="aspect-square rounded-lg overflow-hidden bg-secondary/50">
            <SafeImage
              src={submission.content}
              alt={submission.caption || "Submission"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ) : submission.submissionType === "video" ? (
          <div className="aspect-video rounded-lg overflow-hidden bg-secondary/50">
            <video
              src={submission.content}
              className="w-full h-full object-cover"
              controls={false}
              muted
              loop
              playsInline
              onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
              onMouseLeave={(e) => (e.target as HTMLVideoElement).pause()}
            />
          </div>
        ) : (
          <div className="rounded-lg bg-secondary/50 p-3 min-h-[80px]">
            <div className="flex items-center gap-1.5 mb-1.5">
              <SubmissionTypeIcon type={submission.submissionType} />
              <span className="text-[10px] text-muted-foreground uppercase">
                {submission.submissionType}
              </span>
            </div>
            <p className="text-sm text-foreground/80 line-clamp-3">
              {submission.content}
            </p>
          </div>
        )}
      </div>

      {/* Caption */}
      {submission.caption && !compact && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {submission.caption}
        </p>
      )}

      {/* Actions bar */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={handleVote}
          disabled={voted || voteMutation.isPending}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
            voted
              ? "bg-neon/20 text-neon cursor-default"
              : "bg-secondary hover:bg-neon/20 hover:text-neon cursor-pointer"
          }`}
        >
          <motion.div animate={animating ? { scale: [1, 1.4, 1] } : {}}>
            <Heart className={`w-3.5 h-3.5 ${voted ? "fill-teal" : ""}`} />
          </motion.div>
          <span>{submission.voteCount + (voted ? 1 : 0)}</span>
        </button>

        <button
          onClick={handleShare}
          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-neon hover:bg-neon/10 transition-all"
        >
          <Share2 className="w-3 h-3" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>
    </motion.div>
  );
}

export default function EmbeddableGallery({
  gameSlug,
  limit = 12,
  compact = false,
  showHeader = true,
  onVote,
}: EmbeddableGalleryProps) {
  const { data, isLoading, error } = trpc.game.galleryEmbed.useQuery(
    { gameSlug, limit },
    { refetchInterval: 30000 } // Refresh every 30s for live voting
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 border-2 border-neon border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-muted-foreground">Loading submissions...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        Unable to load submissions
      </div>
    );
  }

  const { game, submissions } = data;

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-bold ${compact ? "text-sm" : "text-lg"}`}>
              {game.title}
            </h3>
            {game.description && !compact && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {game.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {game.status === "active" && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-neon" />
                Live
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {submissions.length} entries
            </span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {submissions.length === 0 && (
        <div className="text-center py-12 glass-panel rounded-xl">
          <Trophy className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No submissions yet</p>
          <p className="text-xs text-muted-foreground mt-1">Be the first to enter!</p>
        </div>
      )}

      {/* Gallery grid */}
      <div className={`grid gap-3 ${
        compact
          ? "grid-cols-2"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
      }`}>
        {submissions.map((sub, i) => (
          <SubmissionCard
            key={sub.id}
            submission={sub}
            rank={i + 1}
            compact={compact}
            gameSlug={gameSlug}
            onVote={onVote}
          />
        ))}
      </div>

      {/* Footer with GamePoint branding for embeds */}
      <div className="flex items-center justify-center gap-2 pt-2 border-t border-border/30">
        <span className="text-[10px] text-muted-foreground">Powered by</span>
        <a
          href={`${window.location.origin}/game/${gameSlug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-[10px] text-neon hover:text-neon-light transition-colors"
        >
          GamePoint
          <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
    </div>
  );
}
